import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import config from '../config/network';
import PoolsABI from '../abi/ClusterPools.json';
import { getErrorMessage } from '../utils/helpers';

export function usePools() {
  const { provider, signer } = useWeb3();

  function getPoolsRead() {
    if (!provider) return null;
    return new ethers.Contract(config.contracts.pools, PoolsABI, provider);
  }

  function getPoolsWrite() {
    if (!signer) return null;
    return new ethers.Contract(config.contracts.pools, PoolsABI, signer);
  }

  async function getAllPoolInfo() {
    try {
      const pools = getPoolsRead();
      if (!pools) return null;
      const data = await pools.getAllPoolAccumulations();
      return {
        accumulations: data.accumulations.map(a => a),
        eligibleCounts: data.eligibleCounts.map(e => e.toString()),
        currentRounds: data.currentRounds.map(r => r.toString()),
      };
    } catch {
      return null;
    }
  }

  async function getWeeklyRoyaltyInfo() {
    try {
      const pools = getPoolsRead();
      if (!pools) return null;
      const info = await pools.getWeeklyRoyaltyInfo();
      return {
        accumulation: info.accumulation,
        eligibleCount: info.eligibleCount.toString(),
        currentRound: info.currentRound.toString(),
      };
    } catch {
      return null;
    }
  }

  async function getMonthlyRoyaltyInfo() {
    try {
      const pools = getPoolsRead();
      if (!pools) return null;
      const info = await pools.getMonthlyRoyaltyInfo();
      return {
        accumulation: info.accumulation,
        eligibleCount: info.eligibleCount.toString(),
        currentRound: info.currentRound.toString(),
      };
    } catch {
      return null;
    }
  }

  async function getUserPoolEligibility(uid) {
    try {
      const pools = getPoolsRead();
      if (!pools) return null;
      const elig = await pools.getUserPoolEligibility(uid);
      return {
        pools: elig.pools,
        weekly: elig.weekly,
        monthly: elig.monthly,
      };
    } catch {
      return null;
    }
  }

  async function getUserTotalClaimable(uid) {
    try {
      const pools = getPoolsRead();
      if (!pools) return null;
      const data = await pools.getUserTotalClaimable(uid);
      return {
        poolAmounts: data.poolAmounts.map(a => a),
        weeklyAmount: data.weeklyAmount,
        monthlyAmount: data.monthlyAmount,
        grandTotal: data.grandTotal,
      };
    } catch {
      return null;
    }
  }

  async function claimPool(poolIndex) {
    try {
      const poolsWrite = getPoolsWrite();
      if (!poolsWrite) throw new Error('Wallet not connected');
      const tx = await poolsWrite.claimPool(poolIndex);
      await tx.wait();
      return { success: true };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function claimWeekly() {
    try {
      const poolsWrite = getPoolsWrite();
      if (!poolsWrite) throw new Error('Wallet not connected');
      const tx = await poolsWrite.claimWeeklyRoyalty();
      await tx.wait();
      return { success: true };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function claimMonthly() {
    try {
      const poolsWrite = getPoolsWrite();
      if (!poolsWrite) throw new Error('Wallet not connected');
      const tx = await poolsWrite.claimMonthlyRoyalty();
      await tx.wait();
      return { success: true };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function claimAll() {
    try {
      const poolsWrite = getPoolsWrite();
      if (!poolsWrite) throw new Error('Wallet not connected');
      const tx = await poolsWrite.claimAll();
      await tx.wait();
      return { success: true };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  return {
    getAllPoolInfo, getWeeklyRoyaltyInfo, getMonthlyRoyaltyInfo,
    getUserPoolEligibility, getUserTotalClaimable,
    claimPool, claimWeekly, claimMonthly, claimAll,
  };
}
